import { useState, useEffect, useCallback, useMemo } from 'react'
import { getMatrizEntrega, getAllGroups, getOrgDeportes } from '../../../../lib/supabase'
import { useOrganization } from '../../../../contexts/OrganizationContext'
import { useTemporadaActiva } from '../../pagos/useTemporadaActiva'
import { unirMatrices } from '../../../../lib/uniformesMultiTemporada'

const STORAGE_KEY = 'pedidos_temporadas_ids'

// Carga la necesidad de VARIAS temporadas a la vez, para Pedidos.
//
// `useEntregasData` (las otras pantallas) carga una sola: ahi la temporada es el
// contexto y tiene sentido. En Pedidos no: el sujeto es el pedido, que va a un
// proveedor y puede llevar piezas de FLAG y de TACKLE en la misma orden.
//
// No se toco `useEntregasData` para no arriesgar las cuatro pantallas que dependen
// de el por un cambio que solo necesita esta.
export const useNecesidadMultiTemporada = () => {
  const { org } = useOrganization()
  const { temporadas, temporadaId: temporadaPorDefecto } = useTemporadaActiva()

  const [seleccionadas, setSeleccionadasState] = useState(() => {
    try {
      const guardado = localStorage.getItem(STORAGE_KEY)
      return guardado ? JSON.parse(guardado) : null
    } catch { return null }
  })
  const [matriz, setMatriz] = useState({ prendas: [], jugadores: [], entregas: [], temporadaPrendas: [] })
  const [grupos, setGrupos] = useState([])
  const [orgDeportes, setOrgDeportes] = useState([])
  const [loading, setLoading] = useState(true)

  const abiertas = useMemo(
    () => (temporadas || []).filter((t) => t.estado === 'abierta'),
    [temporadas],
  )

  // Por defecto, TODAS las abiertas: es lo que el club tiene corriendo. Si no hay
  // ninguna abierta se cae a la que useTemporadaActiva haya elegido, para no dejar
  // la pantalla muerta.
  useEffect(() => {
    if (seleccionadas !== null) return
    if (abiertas.length) setSeleccionadasState(abiertas.map((t) => t.id))
    else if (temporadaPorDefecto) setSeleccionadasState([temporadaPorDefecto])
  }, [abiertas, temporadaPorDefecto, seleccionadas])

  // Una temporada guardada que ya no existe (o se cerro) no puede dejar la pantalla
  // pidiendo datos de algo que no esta.
  const vigentes = useMemo(
    () => (seleccionadas || []).filter((id) => (temporadas || []).some((t) => t.id === id)),
    [seleccionadas, temporadas],
  )

  const setSeleccionadas = useCallback((ids) => {
    setSeleccionadasState(ids)
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(ids)) } catch { /* ignore */ }
  }, [])

  const clave = vigentes.join(',')

  const load = useCallback(async (silent = false) => {
    if (!clave) {
      setMatriz({ prendas: [], jugadores: [], entregas: [], temporadaPrendas: [] })
      if (!silent) setLoading(false)
      return
    }
    if (!silent) setLoading(true)
    const ids = clave.split(',')
    const [matrices, gruposRes, deportesRes] = await Promise.all([
      Promise.all(ids.map(async (id) => ({ temporadaId: id, data: (await getMatrizEntrega(id, {})).data }))),
      getAllGroups(),
      getOrgDeportes(org?.id),
    ])
    setMatriz(unirMatrices(matrices))
    setGrupos((gruposRes.data || []).filter((g) => g.estatus === 'Activo'))
    setOrgDeportes(deportesRes.data || [])
    if (!silent) setLoading(false)
  }, [clave, org?.id])

  useEffect(() => { load() }, [load])

  return {
    temporadas, abiertas,
    seleccionadas: vigentes,
    setSeleccionadas,
    matriz, grupos, orgDeportes, loading,
    reload: () => load(true),
  }
}
